import {invalidateOnSuccess, omneticApi} from '@dms/api/base';
import {CompositionPreviewResponseBodySchema} from '@dms/api/shared';

import {
  CompositionPreviewApiArg,
  CompositionPreviewApiResponse,
  CreateCompositionFontApiArg,
  CreateCompositionFontApiResponse,
  DeleteCompositionFontApiArg,
  DeleteCompositionFontApiResponse,
  GenerateCompositionBadgeApiArg,
  GenerateCompositionBadgeApiResponse,
  GetCompositionFontListApiResponse,
} from './types';

export const compositionApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    // A mutation rather than a query: the preview is rendered on demand from a request body
    // (the whole scene) and must never be re-fetched automatically.
    compositionPreview: build.mutation<CompositionPreviewApiResponse, CompositionPreviewApiArg>({
      query: (queryArg) => ({
        url: '/dms/v2/settings/composition/preview',
        method: 'POST',
        body: queryArg.compositionPreviewRequestBody,
      }),
      transformResponse: (response) => {
        const preview = CompositionPreviewResponseBodySchema.parse(response);
        return {...preview, base64Url: `data:image/png;base64,${preview.content}`};
      },
    }),
    // Calls a metered AI image generation; only ever triggered by an explicit user action.
    generateCompositionBadge: build.mutation<
      GenerateCompositionBadgeApiResponse,
      GenerateCompositionBadgeApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v2/settings/composition/badge',
        method: 'POST',
        body: queryArg.generateCompositionBadgeRequestBody,
      }),
      // Paid OpenAI call: a gateway retry would bill a second generation.
      extraOptions: {skipInfrastructureRetry: true},
    }),
    getCompositionFontList: build.query<GetCompositionFontListApiResponse, void>({
      query: () => ({url: '/dms/v1/settings/composition-font'}),
      providesTags: ['CompositionFont'],
    }),
    createCompositionFont: build.mutation<
      CreateCompositionFontApiResponse,
      CreateCompositionFontApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/settings/composition-font',
        method: 'POST',
        body: queryArg.createCompositionFontRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(['CompositionFont']),
    }),
    deleteCompositionFont: build.mutation<
      DeleteCompositionFontApiResponse,
      DeleteCompositionFontApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/composition-font/${encodeURIComponent(queryArg.id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(['CompositionFont']),
    }),
  }),
});

export const {
  useCompositionPreviewMutation,
  useGenerateCompositionBadgeMutation,
  useGetCompositionFontListQuery,
  useCreateCompositionFontMutation,
  useDeleteCompositionFontMutation,
} = compositionApi;
